import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { DsButtonColor, DsButtonIntent, DsButtonVariants, Icons } from '@levelaccess/design-system';

import { CustomValidators } from '../../services/helpers/form-custom-validators';
import { $scanHeaders } from '../../../../shared/constants/scan';
import { IScanHeader } from '../../../../shared/interfaces/scan.interface';
import { FormService } from '../../services/form.service';

enum $fields {
  name = 'name',
  value = 'value',
}

@Component({
  selector: 'app-custom-header',
  templateUrl: './custom-header.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CustomHeaderComponent implements OnInit, OnDestroy {
  @Input()
  public form: UntypedFormGroup;

  @Input()
  public headersControlName: string = 'headers';

  @Input()
  public disabled: boolean = false;

  @Input()
  public nameLabel: string = 'label_header';

  @Input()
  public valueLabel: string = 'label_value';

  @Input()
  public addButtonLabel: string = 'label_add_pair';

  @Input()
  public deleteButtonLabel: string = 'action_delete';

  public readonly DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;
  public readonly Icons: typeof Icons = Icons;
  public readonly DsButtonColor: typeof DsButtonColor = DsButtonColor;
  public readonly DsButtonIntent: typeof DsButtonIntent = DsButtonIntent;
  public $fields: typeof $fields = $fields;
  private subscriptions: Subscription;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private changeDetectorRef: ChangeDetectorRef,
    private formService: FormService,
  ) {
    this.subscriptions = new Subscription();
  }

  @Input()
  public set headers(headers: IScanHeader[]) {
    if (Array.isArray(headers) && headers.length > 0) {
      this.initializeHeadersControl();
      this.headersForm.clear();

      for (const header of headers) {
        this.addControl(header[$scanHeaders.name], header[$scanHeaders.value]);
      }
    }
    this.changeDetectorRef.detectChanges();
  }

  public get headersForm(): UntypedFormArray {
    return this.form?.get(this.headersControlName) as UntypedFormArray;
  }

  public get headersValues(): UntypedFormGroup[] {
    return this.headersForm?.controls as UntypedFormGroup[];
  }

  public ngOnInit(): void {
    this.initializeHeadersControl();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public addControl(name: string = '', value: string = ''): void {
    const nameControl: UntypedFormControl = this.formBuilder.control(name);
    const valueControl: UntypedFormControl = this.formBuilder.control(value);

    const headerGroup: UntypedFormGroup = this.formBuilder.group({
      [$fields.name]: nameControl,
      [$fields.value]: valueControl,
    });

    this.setupBidirectionalValidation(headerGroup);
    this.headersForm.push(headerGroup);

    this.changeDetectorRef.detectChanges();
  }

  private setupBidirectionalValidation(group: UntypedFormGroup): void {
    const nameControl = group.get($fields.name);
    const valueControl = group.get($fields.value);

    const updateValidators = (): void => {
      const nameValue: string = nameControl?.value?.trim();
      const valueValue: string = valueControl?.value?.trim();

      const nameHasValue: boolean = nameValue?.length > 0;
      const valueHasValue: boolean = valueValue?.length > 0;

      if (nameHasValue && !valueHasValue) {
        // Name has value, value is empty → value becomes required
        valueControl.setValidators([CustomValidators.required, CustomValidators.validateIsEmpty]);
        nameControl.clearValidators();
      } else if (!nameHasValue && valueHasValue) {
        // Value has value, name is empty → name becomes required
        nameControl.setValidators([CustomValidators.required, CustomValidators.validateIsEmpty]);
        valueControl.clearValidators();
      } else {
        nameControl.clearValidators();
        valueControl.clearValidators();
      }

      nameControl.updateValueAndValidity({ emitEvent: false });
      valueControl.updateValueAndValidity({ emitEvent: false });
      this.changeDetectorRef.detectChanges();
    };

    this.subscriptions.add(nameControl.valueChanges.subscribe(() => updateValidators()));
    this.subscriptions.add(valueControl.valueChanges.subscribe(() => updateValidators()));
    updateValidators();
  }

  public removeControl(index: number): void {
    this.headersForm.removeAt(index);
    this.form.markAsDirty();
    this.changeDetectorRef.detectChanges();
  }

  public getNameError(index: number): string {
    const control = this.headersValues[index]?.get($fields.name);
    return this.formService.getErrorMessageForField(control, $fields.name, '', this.nameLabel);
  }

  public getValueError(index: number): string {
    const control = this.headersValues[index]?.get($fields.value);
    return this.formService.getErrorMessageForField(control, $fields.value, '', this.valueLabel);
  }

  private initializeHeadersControl(): void {
    if (!this.form.contains(this.headersControlName)) {
      this.form.registerControl(this.headersControlName, this.formBuilder.array([]));
    }
  }
}
